import { useCallback, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Smartphone, CalendarClock, History } from "lucide-react";

import { AuthContext } from "../../../shared/context/auth-context";
import { ClinicaContext } from "../../../shared/context/clinica-context";
import { useLoading } from "../../../shared/context/LoadingContext";

import "./Portal.scss";

/**
 * Configurações do Portal do Paciente.
 *
 * ─── UM INTERRUPTOR MESTRE, E O RESTO SÓ DEPOIS ─────────────────────────────
 *
 * As opções de detalhe só aparecem com o portal ligado. Uma clínica que nunca
 * vai usar isto não tem de perceber o que é «ver histórico de consultas» para
 * decidir que não quer nada disto — vê um interruptor, desliga-o, e acabou.
 *
 * ─── SÓ EXISTE O QUE O PRODUTO FAZ ──────────────────────────────────────────
 *
 * Duas opções, e são as duas coisas que o portal mostra hoje. Não há aqui
 * «permitir marcar» nem «permitir cancelar»: essas funcionalidades não existem,
 * e um interruptor desligado para algo que não existe é uma promessa no ecrã de
 * configurações que o produto não cumpre.
 */
const Portal = ({ sendRequest, clinicaId }) => {
  const auth = useContext(AuthContext);
  const { setConfiguracoes } = useContext(ClinicaContext);
  const { startLoading, stopLoading } = useLoading();

  const [configuracao, setConfiguracao] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/configuracoes/clinica/${clinicaId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      setConfiguracao(resposta.configuracao);
    } catch (err) {
      console.error("Erro ao carregar configurações do portal:", err);
    }
  }, [auth.token, clinicaId, sendRequest]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Alterna um dos interruptores.
   *
   * `campo` vazio significa o `ativo` — o interruptor mestre. O endpoint aceita
   * os dois e é o mesmo caminho para ambos, com o mesmo registo no histórico da
   * clínica.
   */
  const alternar = async (campo) => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("utilizador", auth.userId);
      formData.append("nomeConfiguracao", "portalPaciente");
      if (campo) formData.append("campo", campo);

      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/configuracoes/ativardesativar/${clinicaId}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      setConfiguracao(resposta.configuracao);
      setConfiguracoes?.(resposta.configuracao);
      toast.success(resposta.message);
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
  };

  const portal = configuracao?.configuracoes?.portalPaciente;
  const ativo = portal?.ativo === true;

  /* `!== false` e não `=== true`: estes dois nascem ligados no schema, e nas
     clínicas cujo documento ainda não tem o campo o valor chega `undefined`.
     Com `=== true` o ecrã mostrava-os desligados quando na verdade estão
     ligados. */
  const verProximas = portal?.verProximasConsultas !== false;
  const verHistorico = portal?.verHistorico !== false;

  const Interruptor = ({ ligado, aoMudar, id }) => (
    <label className="portalConfig__interruptor" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={ligado}
        onChange={aoMudar}
      />
      <span className="portalConfig__interruptor__calha">
        <span className="portalConfig__interruptor__bola" />
      </span>
    </label>
  );

  return (
    <div className="portalConfig">
      <div className="portalConfig__cabecalho">
        <Smartphone size={20} />
        <h2>Portal do Paciente</h2>
      </div>

      <p className="portalConfig__intro">
        O paciente recebe uma mensagem com um código, entra no telemóvel e vê as
        suas marcações. Só consulta: não marca, não cancela e não vê valores.
      </p>

      <div className="portalConfig__mestre">
        <div>
          <strong>Ativar o portal para esta clínica</strong>
          <p>
            Enquanto estiver desligado, esta clínica não existe para o portal —
            um paciente que tente entrar não recebe código nenhum.
          </p>
        </div>
        <Interruptor
          id="portal-ativo"
          ligado={ativo}
          aoMudar={() => alternar(null)}
        />
      </div>

      {ativo && (
        <div className="portalConfig__opcoes">
          <h3>O que o paciente pode ver</h3>

          <div className="portalConfig__opcao">
            <CalendarClock size={18} />
            <div>
              <strong>Próximas consultas</strong>
              <p>Data, hora, profissional, tratamento e morada da unidade.</p>
            </div>
            <Interruptor
              id="portal-proximas"
              ligado={verProximas}
              aoMudar={() => alternar("verProximasConsultas")}
            />
          </div>

          <div className="portalConfig__opcao">
            <History size={18} />
            <div>
              <strong>Consultas passadas</strong>
              <p>As últimas 20 marcações já realizadas.</p>
            </div>
            <Interruptor
              id="portal-historico"
              ligado={verHistorico}
              aoMudar={() => alternar("verHistorico")}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Portal;
